import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import OwnerPageLayout from '../components/layout/OwnerPageLayout';
import LandingHeader from '../components/LandingHeader';
import LandingHeroBackground from '../components/LandingHeroBackground';
import SiteFooter from '../components/SiteFooter';
import BetaBanner from '../components/BetaBanner';
import BetaPopup from '../components/BetaPopup';
import { SportCard } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Logo from '../components/Logo';
import { getApiBaseUrl } from '../apiBase';
import { isInternationalDomain, isRussianDomain } from '../config/domainLanguages';
import { getSchoolForSession, getSchoolIdFromResponse, isBenignSchoolLoadError } from '../utils/schoolHelper';

const API = getApiBaseUrl();

const ROLE_ICONS = {
    Admin: '🛡️',
    Owner: '👑',
    Coach: '🎽',
    Member: '⚽',
};

const ROLE_STYLE = {
    Admin: { color: '#EB3333', bg: '#f0ebff' },
    Owner: { color: '#EB3333', bg: '#FFF5F5' },
    Coach: { color: '#EB3333', bg: '#e3f2fd' },
    Member: { color: '#e65100', bg: '#fff3e0' },
};

const MENU_CONFIG = {
    Owner: [
        { icon: '🏫', bg: '#f0ebff', href: '/school', key: 'school' },
        { icon: '⚙️', bg: '#fff8e1', href: '/school/settings', key: 'settings' },
        { icon: '🔗', bg: '#eff6ff', href: '/school/settings/integrations', key: 'integrations' },
        { icon: '👥', bg: '#e3f2fd', href: '/students', key: 'students' },
        { icon: '🧑‍💼', bg: '#eef2ff', href: '/staff', key: 'staff' },
        { icon: '📅', bg: '#e8f5e9', href: '/attendance', key: 'attendance' },
        { icon: '🏁', bg: '#ede9fe', href: '/events', key: 'events' },
        { icon: '🎫', bg: '#fff3e0', href: '/subscriptions', key: 'subscriptions' },
        { icon: '🎬', bg: '#fce4ec', href: '/upload', key: 'upload' },
        { icon: '📊', bg: '#e0f7fa', href: '/reports', key: 'reports' },
    ],
    Coach: [
        { icon: '👥', bg: '#e3f2fd', href: '/students', key: 'students' },
        { icon: '📅', bg: '#e8f5e9', href: '/attendance', key: 'attendance' },
        { icon: '🗓️', bg: '#eef2ff', href: '/schedule', key: 'schedule' },
        { icon: '🎬', bg: '#fce4ec', href: '/upload', key: 'upload' },
    ],
    Member: [
        { icon: '💳', bg: '#f3eeff', href: '/my-balance', key: 'balance' },
        { icon: '📅', bg: '#e8f5e9', href: '/attendance', key: 'attendance' },
        { icon: '🏠', bg: '#fce4ec', href: '/', key: 'home' },
    ],
    Admin: [
        { icon: '💎', bg: '#ede9fe', href: '/admin/school-billing', key: 'billing' },
        { icon: '📧', bg: '#fff5f5', href: '/admin/email-settings', key: 'emailSettings' },
        { icon: '🔑', bg: '#fef3c7', href: '/admin/integrations', key: 'integrations' },
        { icon: '📚', bg: '#e0f2fe', href: '/admin/references', key: 'references' },
        { icon: '🏫', bg: '#f0ebff', href: '/school', key: 'school' },
        { icon: '🧑‍💼', bg: '#eef2ff', href: '/staff', key: 'staff' },
        { icon: '👥', bg: '#e3f2fd', href: '/students', key: 'students' },
        { icon: '📅', bg: '#e8f5e9', href: '/attendance', key: 'attendance' },
        { icon: '📊', bg: '#e0f7fa', href: '/reports', key: 'reports' },
        { icon: '🎫', bg: '#fff3e0', href: '/subscriptions', key: 'subscriptions' },
    ],
};

const QUICK_ACTION_KEYS = {
    Owner: ['addStudent', 'staff', 'newEvent', 'attendance', 'subscriptions', 'upload'],
    Coach: ['attendance', 'students', 'upload'],
    Member: ['balance', 'home'],
    Admin: ['schools', 'staff', 'students'],
};

const QUICK_ACTION_HREF = {
    addStudent: '/students',
    staff: '/staff',
    newEvent: '/events',
    attendance: '/attendance',
    subscriptions: '/subscriptions',
    upload: '/upload',
    students: '/students',
    balance: '/my-balance',
    home: '/',
    schools: '/school',
};

const QUICK_ACTION_ICONS = {
    addStudent: '➕',
    staff: '🧑‍💼',
    newEvent: '🏁',
    attendance: '📅',
    subscriptions: '🎫',
    upload: '🎬',
    students: '👥',
    balance: '💳',
    home: '🏠',
    schools: '🏫',
};

const FEATURE_KEYS = ['trial', 'schedule', 'attendance', 'payments', 'reports', 'video', 'mobile', 'map', 'subscription'];

const SPORT_KEYS = ['hockey', 'figure', 'football', 'gymnastics', 'dance', 'swim', 'martial', 'more'];
const SPORT_ICONS = {
    hockey: '🏒',
    figure: '⛸️',
    football: '⚽',
    gymnastics: '🤸',
    dance: '💃',
    swim: '🏊',
    martial: '🥋',
    more: '✨',
};

export default function HomePage() {
    const token = localStorage.getItem('token');
    const username = localStorage.getItem('username');
    const role = localStorage.getItem('role') || 'Member';

    if (!token) return <LandingPage />;
    return <Dashboard username={username} role={role} />;
}

function LandingPage() {
    const { t, i18n } = useTranslation(['landing', 'features', 'common', 'demo']);
    const showBeta = isInternationalDomain();
    const isRu = isRussianDomain();
    const [showBetaPopup, setShowBetaPopup] = useState(false);

    useEffect(() => {
        if (!showBeta) return undefined;
        const applied = localStorage.getItem('beta_applied');
        if (!applied) {
            const timer = setTimeout(() => setShowBetaPopup(true), 5000);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [showBeta]);

    const pickFeature = (key, field) => {
        if (!isRu && (key === 'trial' || key === 'payments')) {
            const intlVal = t(`features:${key}.${field}Intl`, { defaultValue: '' });
            if (intlVal && !intlVal.endsWith('Intl')) return intlVal;
        }
        return t(`features:${key}.${field}`);
    };

    const pick = (baseKey, intlKey) => (
        !isRu ? t(intlKey, { defaultValue: t(baseKey) }) : t(baseKey)
    );

    const services = useMemo(
        () => ['school', 'finance', 'video', 'platform'].map((key) => ({
            icon: { school: '🏫', finance: '💰', video: '🎬', platform: '💎' }[key],
            title: t(`landing:services.${key}.title`),
            description: pick(
                `landing:services.${key}.description`,
                `landing:servicesPublic.${key}.description`,
            ),
        })),
        [t, isRu, i18n.language]
    );

    const features = useMemo(
        () => FEATURE_KEYS.map((key) => ({
            key,
            icon: t(`features:${key}.icon`),
            title: pickFeature(key, 'title'),
            desc: pickFeature(key, 'desc'),
        })),
        [t, i18n.language, isRu]
    );

    const releaseNoteItems = useMemo(() => {
        const items = t('landing:releaseNotes.items', { returnObjects: true });
        return Array.isArray(items) ? items : [];
    }, [t, i18n.language]);

    const audienceCards = useMemo(() => {
        const cards = t('landing:audience.cards', { returnObjects: true });
        return Array.isArray(cards) ? cards : [];
    }, [t, i18n.language]);

    const sports = useMemo(
        () => SPORT_KEYS.map((key) => ({
            icon: SPORT_ICONS[key],
            name: t(`landing:sports.${key}`),
        })),
        [t, i18n.language]
    );

    const heroTrialNote = isRu
        ? t('landing:hero.trialNote')
        : t('landing:freeTrialBadge');
    const ctaTitle = pick('landing:cta.title', 'landing:readyToStart');

    return (
        <div className="sp-landing">
            {showBeta && <BetaBanner onOpenPopup={() => setShowBetaPopup(true)} />}
            <LandingHeader />

            <section className="sp-landing-hero">
                <LandingHeroBackground variant="landing" />
                <div className="sp-landing-hero__content">
                    <span className="sp-landing-hero__badge">{t('landing:hero.badge')}</span>
                    <Logo variant="dark" height={56} style={{ marginBottom: 24, marginLeft: 'auto', marginRight: 'auto' }} />
                    <p className="sp-landing-hero__subtitle">{t('landing:hero.subtitle')}</p>
                    <p className="sp-landing-hero__note">{heroTrialNote}</p>
                    <div className="sp-landing-hero__actions">
                        <Button variant="white" href="/register">{t('landing:hero.ctaRegister')}</Button>
                        <Button variant="outline" href="/pricing" style={{ borderColor: 'white', color: 'white' }}>{t('landing:hero.ctaPricing')}</Button>
                        <Button variant="ghost" href="/login" style={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)' }}>{t('landing:hero.ctaLogin')}</Button>
                        <Button variant="ghost" href="/demo-login" style={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)' }}>{t('demo:tryDemo')}</Button>
                    </div>
                    <div className="sp-store-badges">
                        <a
                            className="sp-store-badge"
                            href="https://play.google.com/apps/internaltest/4701482663822499913"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <span className="sp-store-badge__icon" aria-hidden>▶️</span>
                            <span>{t('landing:getOnGooglePlay')}</span>
                        </a>
                        <a
                            className="sp-store-badge"
                            href="https://testflight.apple.com/join/FZ5RPxcf"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <span className="sp-store-badge__icon" aria-hidden>🍎</span>
                            <span>{t('landing:testOnTestFlight')}</span>
                        </a>
                    </div>
                </div>
            </section>

            <div className="sp-landing-services">
                {services.map((s) => (
                    <SportCard key={s.title} icon={s.icon} title={s.title} description={s.description} />
                ))}
            </div>

            <section className="sp-landing-section">
                <h2 className="sp-section-title" style={{ textAlign: 'center' }}>{t('landing:featuresTitle')}</h2>
                <p style={{ textAlign: 'center', color: 'var(--sp-gray-600)', marginBottom: '44px', fontSize: '17px' }}>
                    {t('landing:featuresSubtitle')}
                </p>
                <div className="sp-landing-feature-grid">
                    {features.map((f) => (
                        <article key={f.key} className="sp-landing-feature">
                            <div style={{ fontSize: '36px', marginBottom: '12px' }}>{f.icon}</div>
                            <h3>{f.title}</h3>
                            <p>{f.desc}</p>
                        </article>
                    ))}
                </div>
            </section>

            <section className="sp-landing-section">
                <h2 className="sp-section-title" style={{ textAlign: 'center' }}>{t('landing:audience.title')}</h2>
                <div className="sp-landing-feature-grid" style={{ marginTop: '36px' }}>
                    {audienceCards.map((card, i) => (
                        <article key={i} className="sp-landing-feature">
                            <div style={{ fontSize: '36px', marginBottom: '12px' }}>{card.icon}</div>
                            <h3>{card.title}</h3>
                            <p>{card.text}</p>
                        </article>
                    ))}
                </div>
            </section>

            <section className="sp-landing-section--dark">
                <h2 className="sp-heading" style={{ color: 'white', fontSize: 32, marginBottom: 10 }}>{t('landing:sportsTitle')}</h2>
                <p style={{ color: 'var(--sp-gray-400)', marginBottom: '40px' }}>{t('landing:sportsSubtitle')}</p>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap' }}>
                    {sports.map((s, i) => (
                        <div key={i} style={{
                            background: 'rgba(255,255,255,0.08)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            borderRadius: '12px', padding: '16px 24px',
                            textAlign: 'center', minWidth: '100px'
                        }}>
                            <div style={{ fontSize: '32px', marginBottom: '8px' }}>{s.icon}</div>
                            <div style={{ fontSize: '13px', color: '#94a3b8' }}>{s.name}</div>
                        </div>
                    ))}
                </div>
            </section>

            <section className="sp-landing-cta">
                <h2 className="sp-section-title" style={{ textAlign: 'center' }}>{ctaTitle}</h2>
                <p style={{ color: 'var(--sp-gray-600)', marginBottom: '32px', fontSize: '17px', maxWidth: '520px', marginLeft: 'auto', marginRight: 'auto' }}>
                    {t('landing:cta.body')}{' '}
                    <a href="/pricing" style={{ color: 'var(--sp-primary)' }}>{t('landing:cta.pricingLink')}</a>
                </p>
                <Button href="/register">{t('landing:cta.button')}</Button>
            </section>

            <div style={{
                padding: '40px 40px 48px', maxWidth: '900px', margin: '0 auto',
                borderTop: '1px solid var(--sp-gray-100)'
            }}>
                <h3 style={{ textAlign: 'center', margin: '0 0 6px', fontSize: '20px', fontWeight: 700, color: '#475569' }}>
                    {t('landing:releaseNotes.title')}
                </h3>
                <p style={{ textAlign: 'center', margin: '0 0 20px', fontSize: '13px', color: '#94a3b8' }}>
                    {t('landing:releaseNotes.subtitle')}
                </p>
                <ul className="feature-list" style={{
                    margin: 0, padding: 0, listStyle: 'none',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                    gap: '8px 24px'
                }}>
                    {releaseNoteItems.map((line, i) => (
                        <li key={i} style={{
                            fontSize: '13px', color: '#64748b', lineHeight: 1.45,
                            paddingLeft: '14px', position: 'relative'
                        }}>
                            <span style={{
                                position: 'absolute', left: 0, color: '#EB3333', fontWeight: 700
                            }}>•</span>
                            {line}
                        </li>
                    ))}
                </ul>
            </div>

            <SiteFooter />
            {showBeta && showBetaPopup && (
                <BetaPopup
                    onClose={() => {
                        setShowBetaPopup(false);
                        localStorage.setItem('beta_applied', 'true');
                    }}
                />
            )}
        </div>
    );
}

function Dashboard({ username, role }) {
    const { t } = useTranslation(['dashboard', 'common']);
    const [school, setSchool] = useState(null);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    const roleKey = { Admin: 'admin', Owner: 'owner', Coach: 'coach', Member: 'member' }[role] || 'member';
    const roleLabel = t(`roles.${roleKey}`);
    const roleIcon = ROLE_ICONS[role] || '👤';
    const roleStyle = ROLE_STYLE[role] || ROLE_STYLE.Member;

    const token = localStorage.getItem('token');
    const api = axios.create({
        baseURL: API,
        headers: { Authorization: `Bearer ${token}` }
    });

    useEffect(() => {
        loadData();
    }, []);

    async function loadData() {
        if (role === 'Admin') {
            setLoading(false);
            return;
        }
        try {
            const schoolRes = await getSchoolForSession(api);
            setSchool(schoolRes.data);
            const schoolId = getSchoolIdFromResponse(schoolRes);
            if (schoolId) {
                const groupsRes = await api.get(`/school/${schoolId}/groups`);
                const groups = groupsRes.data;
                const studentCount = groups.reduce((sum, g) => sum + (g.studentCount || 0), 0);
                setStats({ groupCount: groups.length, studentCount, groups });
            }
        } catch (err) {
            if (!isBenignSchoolLoadError(err)) {
                console.error('Home dashboard load', err);
            }
        }
        setLoading(false);
    }

    const menuItems = useMemo(() => {
        const list = MENU_CONFIG[role] || MENU_CONFIG.Member;
        return list.map((item) => ({
            ...item,
            title: t(`menu.${roleKey}.${item.key}.title`),
            desc: t(`menu.${roleKey}.${item.key}.desc`),
        }));
    }, [role, roleKey, t]);

    const quickActions = useMemo(() => {
        const keys = QUICK_ACTION_KEYS[role] || QUICK_ACTION_KEYS.Member;
        return keys.map((key, i) => ({
            icon: QUICK_ACTION_ICONS[key],
            label: t(`actions.${roleKey}.${key}`),
            href: QUICK_ACTION_HREF[key],
            primary: i === 0,
        }));
    }, [role, roleKey, t]);

    if (loading) {
        return (
            <OwnerPageLayout maxWidth={1100}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '40vh' }}>
                    <div style={{ fontSize: '18px', color: '#888' }}>{t('common:loading')}</div>
                </div>
            </OwnerPageLayout>
        );
    }

    return (
        <OwnerPageLayout maxWidth={1100}>
            <div data-onboarding="dashboard-main" className="dashboard-welcome-banner">
                <div>
                    <h2 className="dashboard-welcome-banner__title" style={{ color: roleStyle.color }}>
                        {roleIcon} {t('welcome', { name: username })} — {roleLabel}
                    </h2>
                    <p className="dashboard-welcome-banner__sub">
                        {school
                            ? t('schoolLine', { name: school.name, sport: school.sportType })
                            : t('noSchool')}
                    </p>
                </div>
                {!school && (
                    <a href="/school" className="sp-btn sp-btn--white btn-create-school btn-cta">
                        {t('createSchool')}
                    </a>
                )}
            </div>

            {stats && (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                    gap: '16px', marginBottom: '28px'
                }}>
                    <StatCard icon="👥" value={stats.studentCount} label={t('stats.students')} />
                    <StatCard icon="📚" value={stats.groupCount} label={t('stats.groups')} />
                </div>
            )}

            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '16px', marginBottom: '28px'
            }}>
                {menuItems.map((item, i) => (
                    <a key={i} href={item.href} className="dashboard-menu-card">
                        <div className="dashboard-menu-card__icon">{item.icon}</div>
                        <div>
                            <div style={{ fontWeight: 'bold', fontSize: '15px', marginBottom: '4px' }}>{item.title}</div>
                            <div style={{ fontSize: '13px', color: 'var(--sp-gray-400)', lineHeight: '1.5' }}>{item.desc}</div>
                        </div>
                    </a>
                ))}
            </div>

            <div className="sp-panel">
                <h3 className="sp-panel__title">⚡ {t('quickActions')}</h3>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {quickActions.map((a, i) => (
                        <a
                            key={i}
                            href={a.href}
                            className={a.primary ? 'dashboard-quick-btn--primary' : ''}
                            style={{
                                padding: '10px 20px',
                                borderRadius: '100px',
                                background: a.primary ? undefined : 'var(--sp-gray-50)',
                                color: a.primary ? undefined : 'var(--sp-gray-600)',
                                fontSize: '14px',
                                textDecoration: 'none',
                                border: '1px solid ' + (a.primary ? 'var(--sp-primary)' : 'var(--sp-gray-200)'),
                                fontWeight: 700,
                            }}
                        >
                            {a.icon} {a.label}
                        </a>
                    ))}
                </div>
            </div>

            <SiteFooter />
        </OwnerPageLayout>
    );
}

function StatCard({ icon, value, label }) {
    return (
        <div className="dashboard-stat-card">
            <div style={{ fontSize: '28px', marginBottom: '6px' }}>{icon}</div>
            <div className="dashboard-stat-card__value">{value}</div>
            <div style={{ fontSize: 13, color: 'var(--sp-gray-400)', marginTop: 4, textTransform: 'uppercase', fontWeight: 700 }}>{label}</div>
        </div>
    );
}
